<template>
  <div class="offer-detail">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t('offers.details') }}</h2>
      <div class="d-flex gap-2">
        <b-button variant="primary" @click="editOffer">
          <i class="fas fa-edit mr-2"></i>{{ $t('common.edit') }}
        </b-button>
        <b-button variant="secondary" @click="goBack">
          <i class="fas fa-arrow-left mr-2"></i>{{ $t('common.back') }}
        </b-button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner></b-spinner>
      <p class="mt-2">{{ $t('offers.loading_offer_details') }}</p>
    </div>

    <div v-else-if="offer" class="row">
      <!-- Basic Information -->
      <div class="col-md-8">
        <b-card class="mb-4">
          <template #header>
            <h5 class="mb-0">
              <i class="fas fa-info-circle mr-2"></i>
              {{ $t('offers.basic_information') }}
            </h5>
          </template>

          <div class="row">
            <div class="col-md-6">
              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.name') }}:</label>
                <p>{{ offer.name }}</p>
              </div>

              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.description') }}:</label>
                <div v-if="offer.description" v-html="offer.description" class="description-content"></div>
                <p v-else class="text-muted">{{ $t('offers.no_description') }}</p>
              </div>

              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.type') }}:</label>
                <span class="badge" :class="getTypeBadgeClass(offer.type)">
                  {{ getTypeDisplayName(offer.type) }}
                </span>
              </div>

              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.value') }}:</label>
                <p>{{ formatValue(offer) }}</p>
              </div>
            </div>

            <div class="col-md-6">
              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.status') }}:</label>
                <span class="badge" :class="offer.is_active ? 'bg-success' : 'bg-secondary'">
                  {{ offer.is_active ? $t('offers.active') : $t('offers.inactive') }}
                </span>
              </div>

              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.hotel_id') }}:</label>
                <p>{{ offer.hotel ? offer.hotel.name : $t('offers.all_hotels') }}</p>
              </div>

              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.promo_code') }}:</label>
                <p>{{ offer.promo_code || $t('offers.no_promo_code') }}</p>
              </div>

              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.max_discount') }}:</label>
                <p>{{ offer.max_discount ? `$${offer.max_discount}` : $t('offers.no_limit') }}</p>
              </div>
            </div>
          </div>
        </b-card>

        <!-- Terms & Conditions -->
        <b-card v-if="offer.terms_conditions" class="mb-4">
          <template #header>
            <h5 class="mb-0">
              <i class="fas fa-file-contract mr-2"></i>
              {{ $t('offers.terms_conditions') }}
            </h5>
          </template>

          <div class="terms-content"><span v-html="offer.terms_conditions"></span></div>
        </b-card>

        <!-- Conditions -->
        <b-card class="mb-4">
          <template #header>
            <h5 class="mb-0">
              <i class="fas fa-cog mr-2"></i>
              {{ $t('offers.conditions_restrictions') }}
            </h5>
          </template>

          <div class="row">
            <div class="col-md-6">
              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.min_order_amount') }}:</label>
                <p>{{ offer.min_order_amount ? `$${offer.min_order_amount}` : $t('offers.no_minimum') }}</p>
              </div>

              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.min_nights') }}:</label>
                <p>{{ offer.min_nights || $t('offers.no_minimum') }}</p>
              </div>

              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.max_nights') }}:</label>
                <p>{{ offer.max_nights || $t('offers.no_maximum') }}</p>
              </div>
            </div>

            <div class="col-md-6">
              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.usage_limit') }}:</label>
                <p>{{ offer.usage_limit || $t('offers.unlimited') }}</p>
              </div>

              <!-- <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.usage_limit_per_user') }}:</label>
                <p>{{ offer.usage_limit_per_user || $t('offers.unlimited') }}</p>
              </div> -->

              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.day_of_week') }}:</label>
                <p v-if="offer.day_of_week && offer.day_of_week.length">
                  {{ formatDaysOfWeek(offer.day_of_week) }}
                </p>
                <p v-else>{{ $t('offers.all_days') }}</p>
              </div>
            </div>
          </div>
        </b-card>

        <!-- Pre-Checkin Settings -->
        <b-card v-if="offer.apply_during_precheckin" class="mb-4">
          <template #header>
            <h5 class="mb-0">
              <i class="fas fa-clock mr-2"></i>
              {{ $t('offers.pre_checkin_settings') }}
            </h5>
          </template>

          <div class="row">
            <div class="col-md-6">
              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.precheckin_discount_type') }}:</label>
                <span class="badge" :class="getTypeBadgeClass(offer.precheckin_discount_type)">
                  {{ getTypeDisplayName(offer.precheckin_discount_type) }}
                </span>
              </div>

              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.precheckin_min_hours_before') }}:</label>
                <p>{{ offer.precheckin_min_hours_before || 24 }} {{ $t('common.hours') || 'hours' }}</p>
              </div>
            </div>


          </div>
        </b-card>

        <!-- Validity Period -->
        <b-card class="mb-4">
          <template #header>
            <h5 class="mb-0">
              <i class="fas fa-calendar mr-2"></i>
              {{ $t('offers.validity_period') }}
            </h5>
          </template>

          <div class="row">
            <div class="col-md-6">
              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.starts_at') }}:</label>
                <p>{{ offer.starts_at ? formatDate(offer.starts_at) : $t('offers.no_start_date') }}</p>
              </div>
            </div>

            <div class="col-md-6">
              <div class="detail-item">
                <label class="font-weight-bold">{{ $t('offers.expires_at') }}:</label>
                <p>{{ offer.expires_at ? formatDate(offer.expires_at) : $t('offers.no_end_date') }}</p>
              </div>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Statistics -->
      <div class="col-md-4">
        <b-card class="mb-4">
          <template #header>
            <h5 class="mb-0">
              <i class="fas fa-chart-bar mr-2"></i>
              {{ $t('offers.statistics') }}
            </h5>
          </template>

          <div class="stats-item">
            <div class="stat-value">{{ offer.usages_count || 0 }}</div>
            <div class="stat-label">{{ $t('offers.total_uses') }}</div>
          </div>

          <div class="stats-item">
            <div class="stat-value">{{ offer.usage_limit || '∞' }}</div>
            <div class="stat-label">{{ $t('offers.usage_limit_label') }}</div>
          </div>

          <div class="stats-item">
            <div class="stat-value">{{ getRemainingUses() }}</div>
            <div class="stat-label">{{ $t('offers.remaining_uses') }}</div>
          </div>
        </b-card>

        <!-- Room Types -->
        <b-card v-if="offer.room_type_ids && offer.room_type_ids.length" class="mb-4">
          <template #header>
            <h5 class="mb-0">
              <i class="fas fa-bed mr-2"></i>
              {{ $t('offers.applicable_room_types') }}
            </h5>
          </template>

          <div class="room-types-list">
            <div v-for="roomTypeId in offer.room_type_ids" :key="roomTypeId" class="room-type-item">
              <i class="fas fa-bed mr-2"></i>
              {{ getRoomTypeName(roomTypeId) }}
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <div v-else class="text-center py-5">
      <i class="fas fa-exclamation-triangle text-warning" style="font-size: 3rem;"></i>
      <h4 class="mt-3">{{ $t('offers.offer_not_found') }}</h4>
      <p class="text-muted">{{ $t('offers.offer_not_found_message') }}</p>
      <b-button variant="primary" @click="goBack">
        <i class="fas fa-arrow-left mr-2"></i>{{ $t('offers.back_to_offers') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { request } from '@/Util/Request';

export default {
  name: 'OfferDetail',
  data() {
    return {
      offer: null,
      loading: true,
      roomTypes: []
    };
  },
  async created() {
    await this.fetchOffer();
    await this.fetchRoomTypes();
  },
  methods: {
    async fetchOffer() {
      try {
        const response = await request({
          method: 'get',
          url: `/offers/${this.$route.params.id}`
        });
        this.offer = response.data;
      } catch (error) {
        console.error('Error fetching offer:', error);
        this.$bvToast.toast('Error loading offer details', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchRoomTypes() {
      try {
        const response = await request({
          method: 'get',
          url: '/room-types'
        });
        this.roomTypes = response.data || [];
      } catch (error) {
        console.error('Error fetching room types:', error);
      }
    },

    getTypeBadgeClass(type) {
      const classes = {
        'percentage': 'bg-info',
        'fixed': 'bg-primary',
        'free_night': 'bg-success',
        'upgrade': 'bg-warning',
        'package': 'bg-secondary'
      };
      return classes[type] || 'bg-secondary';
    },

    getTypeDisplayName(type) {
      const names = {
        'percentage': 'Percentage',
        'fixed': 'Fixed Amount',
        'free_night': 'Free Night',
        'upgrade': 'Room Upgrade',
        'package': 'Package Deal'
      };
      return names[type] || type;
    },

    formatValue(offer) {
      switch (offer.type) {
        case 'percentage':
          return `${offer.value}%`;
        case 'fixed':
          return `$${offer.value}`;
        case 'free_night':
          return '1 Free Night';
        case 'upgrade':
          return 'Room Upgrade';
        case 'package':
          return `$${offer.value} Package`;
        default:
          return offer.value;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString() + ' ' +
        new Date(dateString).toLocaleTimeString();
    },

    formatDaysOfWeek(days) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days.map(day => dayNames[day]).join(', ');
    },

    getRoomTypeName(roomTypeId) {
      const roomType = this.roomTypes.find(rt => rt.id === roomTypeId);
      return roomType ? roomType.name : `Room Type ${roomTypeId}`;
    },

    getRemainingUses() {
      if (!this.offer.usage_limit) return '∞';
      const used = this.offer.usages_count || 0;
      const remaining = this.offer.usage_limit - used;
      return Math.max(0, remaining);
    },

    editOffer() {
      this.$router.push(`/admin/offers/${this.$route.params.id}/edit`);
    },

    goBack() {
      this.$router.push('/admin/offers');
    }
  }
};
</script>

<style scoped>
.offer-detail {
  padding: 20px;
}

.detail-item {
  margin-bottom: 1rem;
}

.detail-item label {
  color: #6c757d;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.detail-item p {
  margin-bottom: 0;
  font-size: 1rem;
}

.stats-item {
  text-align: center;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.stats-item:last-child {
  border-bottom: none;
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: #007bff;
}

.stat-label {
  color: #6c757d;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.room-types-list {
  max-height: 200px;
  overflow-y: auto;
}

.room-type-item {
  padding: 0.5rem;
  border-bottom: 1px solid #f8f9fa;
}

.room-type-item:last-child {
  border-bottom: none;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.description-content,
.terms-content {
  line-height: 1.6;
  color: #495057;
}

.description-content p,
.terms-content p {
  margin-bottom: 0.75rem;
}

.description-content ul,
.terms-content ul,
.description-content ol,
.terms-content ol {
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
}

.description-content li,
.terms-content li {
  margin-bottom: 0.25rem;
}

.description-content strong,
.terms-content strong {
  font-weight: 600;
  color: #212529;
}

.description-content em,
.terms-content em {
  font-style: italic;
}

.description-content u,
.terms-content u {
  text-decoration: underline;
}
</style>